import { useEffect, useState } from "react";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Datatable from "../../../../core/common/dataTable";
import FiltersTrackCase, {
  FiltersSelectType,
  initialFilterSelect,
  SearchFormDataType,
} from "../FiltersTrackCase";
import {
  parseDate,
  parseDaysString,
  parseNumber,
} from "../../../../core/methods/sorterMethods";
import TimelineModal from "../TimelineModal";
import ModifyInviteModal from "./ModifyInviteModal";
import { loggedinList } from "../ApplicantCaseTracking";
import { dummyData_loggedIn, timelineData_invite } from "../../../../core/data/js/dummy-data";
import { formattedDate } from "../../../../core/methods/formattedDate";
import { PaginationType } from "../../../../types/common-type";
import useLocalStorage from "../../../../core/customhook/hooks/useLocalStorage";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";


type Props = {
  status_id?: string;
};

const ApplicantLoggedIn: React.FC<Props> = ({ status_id }) => {
  const { setLocalStorage, getLocalStorage } = useLocalStorage();

  const [itemList, setItemList] = useState(dummyData_loggedIn);
  const [searchFilter, setSearchFilter] = useState<SearchFormDataType>({
    case_id: "",
    applicant_name: "",
    email: "",
    from_date: "",
    to_date: "",
  });

  const [searchTrigger, setSearchTrigger] = useState(0);
  const [filterData, setFilterData] = useState<FiltersSelectType>(initialFilterSelect);

  const [showTimeline, setShowTimeline] = useState<boolean>(false);
  const [showModifyInvite, setShowModifyInvite] = useState<boolean>(false);


  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000, // limit of records
    total: 0,
  });


  // API
  const {
    callApi: handlesSearch,
    data: dataList,
    loading: searchLoading,
  } = useApi(webservice_url.search_live_case_progress, {
    method: "POST",
    autoCall: false,
  });


  const handleTableChange = async (pagination: any) => {
    setPagination((prev) => ({
      ...prev,
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  const fetchData = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
  ) => {
    const payload = {
      status: status_id || null,
      org_ids: filterData?.organization || null,
      project_ids: filterData?.project || null,
      program_ids: filterData?.program || null,
      department_ids: filterData?.departments || null,
      page,
      limit: pageSize,
    };

    try {
      const response = await handlesSearch(payload);

      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: response?.data?.total || 0,
      }));
    } catch (error) {
      console.error("API Failed", error);
    }
  };


  useEffect(() => {
    if (dataList?.data?.rows) {
      // setItemList(dataList.data.rows);
    }
  }, [dataList]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
  }, [searchTrigger]);


  useEffect(() => {
    const storedFilters = getLocalStorage("admin-live-case-filters");
    // console.log(storedFilters)
    if (storedFilters) {
      setFilterData(storedFilters);
    }
  }, []);



  // ---- columns ----
  const columns: ColumnsType<any> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      render: (text: string, record: any, index: number) => index + 1,
      sorter: (a: any, b: any) => a.sno - b.sno,
    },
    {
      title: "Applicant Name",
      dataIndex: "applicantName",
      key: "applicantName",
      onCell: () => cellWidth(200),
      sorter: (a: any, b: any) =>
        a.applicantName.localeCompare(b.applicantName),
    },
    {
      title: "Case ID",
      dataIndex: "caseId",
      key: "caseId",
      onCell: () => cellWidth(190),
      sorter: (a: any, b: any) => parseNumber(a.caseId) - parseNumber(b.caseId),
    },
    {
      title: "Case Spoc",
      dataIndex: "caseSpoc",
      key: "caseSpoc",
      onCell: () => cellWidth(140),
      sorter: (a: any, b: any) => a.caseSpoc.localeCompare(b.caseSpoc),
    },
    {
      title: "Candidate Id",
      dataIndex: "employeeId",
      key: "employeeId",
      onCell: () => cellWidth(160),
      sorter: (a: any, b: any) =>
        parseNumber(a.employeeId) - parseNumber(b.employeeId),
    },
    {
      title: "Email ID",
      dataIndex: "emailId",
      key: "emailId",
      sorter: (a: any, b: any) => a.emailId.localeCompare(b.emailId),
    },
    {
      title: "Days Elapsed",
      dataIndex: "daysElapsed",
      key: "daysElapsed",
      onCell: () => cellWidth(170),
      sorter: (a: any, b: any) =>
        parseDaysString(a.daysElapsed) - parseDaysString(b.daysElapsed),
    },
    {
      title: "Phone No.",
      dataIndex: "phone",
      key: "phone",
      onCell: () => cellWidth(160),
      render: (text: any) => <div>{text || "-"}</div>,
      sorter: (a: any, b: any) => parseNumber(a.phone) - parseNumber(b.phone),
    },
    {
      title: "Invitation Date",
      dataIndex: "invitationDate",
      key: "invitationDate",
      onCell: () => cellWidth(150),
      render: (text: string) => formattedDate(text) ?? "-",
      sorter: (a: any, b: any) =>
        parseDate(a.invitationDate).getTime() -
        parseDate(b.invitationDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      onCell: () => cellWidth(190),
      render: (status: string) => {
        return (
          <span
            className="badge rounded-pill d-inline-flex align-items-center gap-1 px-3 py-2 fw-medium"
            style={{ background: "#E6F7ED", color: "#0F9D58" }}
          >
            <span
              className="rounded-circle"
              style={{ width: 6, height: 6, background: "#0F9D58" }}
            />
            {status}
          </span>
        );
      },
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
    },
    {
      title: "Invite Expiry Date",
      dataIndex: "inviteExpiryDate",
      key: "inviteExpiryDate",
      onCell: () => cellWidth(180),
      render: (text: string) => formattedDate(text) ?? "-",
      sorter: (a: any, b: any) =>
        parseDate(a.inviteExpiryDate).getTime() -
        parseDate(b.inviteExpiryDate).getTime(),
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   onCell: () => cellWidth(130),
    //   render: () => (
    //     <>
    //       <div className="dropdown">
    //         <button
    //           type="button"
    //           className="btn btn-xs dropdown-toggle"
    //           data-bs-toggle="dropdown"
    //           aria-expanded="false"
    //         >
    //           <EditIcon />
    //           &nbsp;Modify Invite
    //         </button>
    //         <ul className="dropdown-menu ">
    //           <li>
    //             <button
    //               type="button"
    //               className="dropdown-item"
    //               onClick={() => setShowModifyInvite(true)}
    //             >
    //               Fill by HR
    //             </button>
    //           </li>
    //         </ul>
    //       </div>
    //     </>
    //   ),
    // },
    {
      title: "View Log",
      key: "viewLog",
      onCell: () => cellWidth(110),
      render: () => (
        <div
          className="text-center text-dark"
          style={{ cursor: "pointer" }}
          onClick={() => setShowTimeline(true)}
        >
          <i className="ti ti-eye "></i>
        </div>
      ),
    },
  ];


  return (
    <>
      {/* filters */}
      <FiltersTrackCase
        filterData={filterData}
        searchFilter={searchFilter}
        setFilterData={setFilterData}
        setSearchFilter={setSearchFilter}
      />

      {/* Table  */}
      <div className="card mt-3">
        <Datatable
          dataSource={itemList}
          columns={columns}
          Selection={false}
          pagination={pagination}
          handleTableChange={handleTableChange}
        />
      </div>

      {/* Timeline */}
      {showTimeline && (
        <TimelineModal
          timelineData={timelineData_invite}
          onClose={() => setShowTimeline(false)}
        />
      )}

      {/* Modify Invite Modal */}
      {showModifyInvite && (
        <ModifyInviteModal
          handleClose={() => setShowModifyInvite(false)}
          handleSubmit={(d) => {
            console.log(d);
            setShowModifyInvite(false);
          }}
        />
      )}
    </>
  );
};

export default ApplicantLoggedIn;
